import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PeruService, QuoteResultPe } from '../../services/peru.service';

export interface ClientSimPeData {
  countryCode?: string;
  countryName?: string;
}

type Origen = 'USD' | 'VES' | 'CLP';

/** Info de la pata VES→USD cuando el origen es VES. */
interface VesLeg {
  montoVes: number;
  tasaVesUsd: number;
  montoUsd: number;
  /** Tasa única que ve el cliente: PEN por VES. */
  tasaPenVes: number;
}

/** Info de la pata CLP→USD cuando el origen es CLP. */
interface ClpLeg {
  montoClp: number;
  tasaClpUsd: number;
  montoUsd: number;
  /** Tasa única que ve el cliente: PEN por CLP. */
  tasaPenClp: number;
}

/** Mínimo del rail (USD) — mismo que COP. */
const MIN_USD = 20;

/**
 * MODAL "Vista cliente — Envío a Perú": simula la cotización como la vería el
 * cliente (segmento cliente, sin datos internos) + el desglose interno
 * (segmento Aguacate). Origen **USD** (saldo dólar) o **VES** (saldo bolívares,
 * usuario VE): en VES convierte a USD a la tasa configurada (VES→USD) y reusa el
 * mismo motor USD→PEN. Rail único **PE** (PEN, instantáneo). NO expone "Vita".
 */
@Component({
  selector: 'app-rail-peru-client-sim',
  templateUrl: './rail-peru-client-sim.component.html',
  styleUrls: ['./rail-peru-client-sim.component.sass'],
})
export class RailPeruClientSimComponent {
  readonly origenes: { code: Origen; label: string }[] = [
    { code: 'USD', label: 'USD (saldo dólar)' },
    { code: 'VES', label: 'VES (saldo bolívares)' },
    { code: 'CLP', label: 'CLP (saldo pesos)' },
  ];

  origen: Origen = 'USD';
  monto = 100;

  loading = false;
  result: QuoteResultPe | null = null;
  ves: VesLeg | null = null;
  vesActivo = true;
  clp: ClpLeg | null = null;
  clpActivo = true;
  bajoMinimo = false;
  error: string | null = null;

  constructor(
    private svc: PeruService,
    public dialogRef: MatDialogRef<RailPeruClientSimComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClientSimPeData,
  ) {}

  /** Al cambiar de origen, reseteo el monto a un default razonable de su unidad. */
  onOrigenChange(): void {
    this.monto = this.origen === 'VES' ? 100000 : this.origen === 'CLP' ? 100000 : 100;
    this.result = null;
    this.ves = null;
    this.clp = null;
    this.error = null;
  }

  async cotizar(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.result = null;
    this.ves = null;
    this.clp = null;
    this.bajoMinimo = false;
    try {
      let montoUsd: number;
      if (this.origen === 'VES') {
        const live = await this.svc.getVesLive();
        const tasa = live?.rate?.efectiva ?? 0;
        this.vesActivo = !!live?.activo;
        if (!(tasa > 0)) {
          this.error = 'VES→PEN no está configurado (sin tasa). Configuralo en “VES/PEN”.';
          return;
        }
        montoUsd = Number(this.monto) / tasa;
        this.ves = {
          montoVes: Number(this.monto),
          tasaVesUsd: tasa,
          montoUsd,
          tasaPenVes: 0,
        };
      } else if (this.origen === 'CLP') {
        const live = await this.svc.getClpLive();
        const tasa = live?.rate?.efectiva ?? 0;
        this.clpActivo = !!live?.activo;
        if (!(tasa > 0)) {
          this.error = 'CLP→PEN no está configurado (sin tasa). Configuralo en “CLP/PEN”.';
          return;
        }
        montoUsd = Number(this.monto) / tasa;
        this.clp = {
          montoClp: Number(this.monto),
          tasaClpUsd: tasa,
          montoUsd,
          tasaPenClp: 0,
        };
      } else {
        montoUsd = Number(this.monto);
      }
      this.bajoMinimo = montoUsd < MIN_USD;
      const q = await this.svc.quote(montoUsd, 'PE');
      this.result = q;
      if (this.ves && q.resultado && this.ves.montoVes > 0) {
        this.ves.tasaPenVes = q.resultado.recibe / this.ves.montoVes;
      }
      if (this.clp && q.resultado && this.clp.montoClp > 0) {
        this.clp.tasaPenClp = q.resultado.recibe / this.clp.montoClp;
      }
    } catch {
      this.error = 'No se pudo cotizar en este momento.';
    } finally {
      this.loading = false;
    }
  }

  get esVes(): boolean {
    return this.origen === 'VES';
  }
  get esClp(): boolean {
    return this.origen === 'CLP';
  }

  /** Lo que el cliente envía, en su moneda de origen. */
  get montoEnviado(): number | null {
    if (this.esVes) return this.ves?.montoVes ?? null;
    if (this.esClp) return this.clp?.montoClp ?? null;
    return this.result?.resultado?.montoUsd ?? null;
  }

  /** Tasa que ve el cliente: PEN por VES/CLP (ese origen) o PEN por USD (USD). */
  get tasaClienteValor(): number | null {
    if (this.esVes) return this.ves?.tasaPenVes ?? null;
    if (this.esClp) return this.clp?.tasaPenClp ?? null;
    const r = this.result?.resultado;
    return r && r.montoUsd > 0 ? r.recibe / r.montoUsd : null;
  }

  /** digitsInfo del number pipe según la unidad (PEN/VES y PEN/CLP llevan más decimales). */
  get tasaClienteFmt(): string {
    return this.esVes || this.esClp ? '1.4-6' : '1.2-4';
  }

  /** Plazo legible (PEN interbancario = instantáneo; el backend fuerza businessDays=0). */
  get plazoLabel(): string {
    const d = this.result?.businessDays;
    if (d == null) return 'menos de 1 hora';
    if (d <= 0) return 'menos de 1 hora';
    return d === 1 ? '1 día hábil' : `${d} días hábiles`;
  }

  get ofrecible(): boolean {
    return (
      !!this.result?.disponible &&
      this.result?.paisActivo !== false &&
      this.result?.margenOk !== false &&
      (!this.esVes || this.vesActivo) &&
      (!this.esClp || this.clpActivo)
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
